import React, { useState, useEffect, useContext } from 'react';
import { collection, getDocs, doc, deleteDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { MapPin, Clock, Edit, Trash, Eye, Plus, User } from 'lucide-react';
import { AuthContext } from "../../context/AuthContext";
import './styles/routesList.scss';

const RoutesList = ({ onCreateRoute, onEditRoute, onViewDetails, clienteId, servicioId }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { currentService } = useContext(AuthContext);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        setLoading(true);
        
        // Referencia a la subcolección de rutas dentro del servicio actual
        const routesCollection = collection(db, 'servicios', currentService.id, 'rutas');
        
        // Construir la consulta basada en los filtros
        let routesQuery = routesCollection;
        
        // Si hay un cliente seleccionado
        if (clienteId) {
          routesQuery = query(routesCollection, where("clienteId", "==", clienteId));
        }
        
        // Si hay un servicio seleccionado
        if (servicioId) {
          routesQuery = query(routesCollection, where("servicioId", "==", servicioId));
        }
        
        // Si hay ambos filtros
        if (clienteId && servicioId) {
          routesQuery = query(
            routesCollection, 
            where("clienteId", "==", clienteId),
            where("servicioId", "==", servicioId)
          );
        }
        
        const routeSnapshot = await getDocs(routesQuery);
        const routeList = routeSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRoutes(routeList);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching routes:', err);
        setError('Error al cargar las rutas. Por favor, inténtalo de nuevo.');
        setLoading(false);
      }
    };

    if (currentService && currentService.id) {
      fetchRoutes();
    }
  }, [currentService, clienteId, servicioId]);

  const handleDeleteRoute = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta ruta?')) {
      try {
        // Eliminar de la subcolección correcta
        await deleteDoc(doc(db, 'servicios', currentService.id, 'rutas', id));
        setRoutes(routes.filter(route => route.id !== id));
      } catch (err) {
        console.error('Error deleting route:', err);
        setError('Error al eliminar la ruta. Por favor, inténtalo de nuevo.');
      }
    }
  };

  const handleToggleActive = async (route) => {
    try {
      // Actualizar en la subcolección correcta
      const routeRef = doc(db, 'servicios', currentService.id, 'rutas', route.id);
      await updateDoc(routeRef, {
        active: !route.active,
        updatedAt: new Date()
      });
      
      setRoutes(routes.map(r => 
        r.id === route.id ? { ...r, active: !r.active } : r
      ));
    } catch (err) {
      console.error('Error updating route status:', err);
      setError('Error al actualizar el estado de la ruta. Por favor, inténtalo de nuevo.');
    }
  };

  if (loading) {
    return <div className="loading-message">Cargando rutas...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="routes-list-container">
      <div className="list-header">
        <h2>Rutas de Vigilancia</h2>
        <button onClick={onCreateRoute} className="btn-create">
          <Plus size={18} className="icon" />
          Crear Nueva Ruta
        </button>
      </div>

      {routes.length === 0 ? (
        <div className="empty-message">
          {clienteId || servicioId 
            ? "No hay rutas que coincidan con los filtros seleccionados."
            : "No hay rutas creadas. Crea una nueva ruta para comenzar."}
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Ruta</th>
                <th>Cliente</th>
                <th>Servicio</th>
                <th>Puntos</th>
                <th>Horarios</th>
                <th>Estado</th>
                <th className="actions-header">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((route) => (
                <tr key={route.id}>
                  <td>
                    <div className="route-name">{route.name}</div>
                    <div className="route-description">{route.description}</div>
                  </td>
                  <td>
                    <div className="client-name">
                      {route.clienteNombre || route.clienteName || 'No asignado'}
                    </div>
                  </td>
                  <td>
                    <div className="service-name">
                      {route.servicioNombre || route.serviceName || route.servicioNumTienda || 'No asignado'}
                    </div>
                  </td>
                  <td>
                    <div className="checkpoint-count">
                      <MapPin size={16} className="icon" />
                      <span>{route.checkpoints?.length || 0} puntos</span>
                    </div>
                  </td>
                  <td>
                    <div className="schedule-count">
                      <Clock size={16} className="icon" />
                      <span>
                        {route.schedule && route.schedule.reduce((total, day) => 
                          total + (day.times?.length || 0), 0) || 0} horarios
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${route.active ? 'active' : 'inactive'}`}>
                      {route.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td>
                    <div className="actions-container">
                      <button
                        onClick={() => onViewDetails(route)}
                        className="btn-view"
                        title="Ver detalles"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => onEditRoute(route)}
                        className="btn-edit"
                        title="Editar"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(route)}
                        className={`btn-toggle-active ${route.active ? 'active' : 'inactive'}`}
                        title={route.active ? 'Desactivar' : 'Activar'}
                      >
                        <User size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteRoute(route.id)}
                        className="btn-delete"
                        title="Eliminar"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RoutesList;